import type { SecodaEditorComponentProps } from '@repo/secoda-editor';
import { ToastType } from '@repo/secoda-editor';
import dictionary from '@repo/secoda-editor/dictionary';
import copy from 'copy-to-clipboard';
import { NodeSelection } from 'prosemirror-state';
import { memo, useCallback } from 'react';
import { BlockSkeleton } from '../components/BlockSkeleton';
import type { CreateGraphModalResult } from '../components/ChartBlock/CreateGraphModal';
import { QueryBlockContainer } from '../components/QueryBlockContainer/QueryBlockContainer';

export const QueryBlockContainerWrapper = memo(
	({
		onChangeId,
		node,
		getPos,
		isSelected,
		view,
		readOnly,
		onShowToast,
		onTrackEvent,
		shouldRenderPlaceholder,
	}: SecodaEditorComponentProps & {
		readOnly: boolean;
		onShowToast?: (message: string, type: ToastType) => void;
		onTrackEvent?: (
			eventName: string,
			properties?: Record<string, string>
		) => void;
		shouldRenderPlaceholder?: boolean;
		onChangeId: (id: string) => void;
	}) => {
		const deleteNode = useCallback(() => {
			const $pos = view.state.doc.resolve(getPos());
			const tr = view.state.tr.setSelection(new NodeSelection($pos));
			view.dispatch(tr.deleteSelection());
			view.focus();
		}, [getPos, view]);

		const onDuplicate = useCallback(
			(newMetricId: string) => {
				const emptyParagraph = view.state.schema.nodes['paragraph'].create();
				const pos = getPos();

				view.dispatch(
					view.state.tr
						.insert(
							pos + node.nodeSize, // after the current query block
							emptyParagraph
						)
						.insert(
							pos + node.nodeSize + emptyParagraph.nodeSize, // after the empty paragraph created above
							node.type.create({ id: newMetricId })
						)
				);
				view.focus();
				onShowToast?.(dictionary.queryDuplicated, ToastType.Info);
			},
			[getPos, node.nodeSize, node.type, view, onShowToast]
		);

		const copyLink = useCallback(() => {
			const hash = `#metric-${node.attrs.id}`;

			onTrackEvent?.('editor/query-block-copy-link');

			// The existing url might contain a hash already, lets make sure to remove
			// that rather than appending another one.
			const urlWithoutHash = window.location.href.split('#')[0];
			copy(urlWithoutHash + hash);
			onShowToast?.(dictionary.linkCopied, ToastType.Info);
		}, [node.attrs.id, onShowToast, onTrackEvent]);

		const onAddGraph = useCallback(
			(result: CreateGraphModalResult) => {
				const { xAxis, yAxis, dimension, numericFormat } = result;

				const emptyParagraph = view.state.schema.nodes['paragraph'].create();
				const chartBlock = view.state.schema.nodes['chart_block'].create({
					id: node.attrs.id,
					xAxis,
					yAxis,
					dimension,
					numericFormat,
				});
				const pos = getPos();

				view.dispatch(
					view.state.tr
						.insert(
							pos + node.nodeSize, // after the current query block
							emptyParagraph
						)
						.insert(
							pos + node.nodeSize + emptyParagraph.nodeSize, // after the empty paragraph created above
							chartBlock
						)
				);
				view.focus();
			},
			[view, node.attrs.id, node.nodeSize, getPos]
		);

		if (!node) {
			return <BlockSkeleton isSelected={isSelected} height={100} />;
		}

		return (
			<QueryBlockContainer
				id={node.attrs.id}
				onChangeId={onChangeId}
				integrationId={node.attrs.integrationId ?? undefined}
				readOnly={readOnly}
				onDelete={deleteNode}
				isSelected={isSelected}
				onDuplicate={onDuplicate}
				onCopyLink={copyLink}
				onAddGraph={onAddGraph}
				shouldRenderPlaceholder={shouldRenderPlaceholder}
			/>
		);
	}
);
